import { BaseEdge, EdgeLabelRenderer, Handle, Position, getSmoothStepPath } from "@xyflow/react";
import { createContext, useContext } from "react";
import { BRANCH_COLORS } from "./workflowGraph";

/**
 * The blocks of the workflow drawing (user, 2026-10-29), styled after the
 * boss's ChatGPT pictures. Every block is the same width so columns line up.
 * Arrows come in at the top and leave at the bottom.
 */

export const WorkflowContext = createContext(null);

const STATUS_LABEL = { pending: "Pending", working: "Working", done: "Done" };

const In = () => <Handle type="target" position={Position.Top} className="wf-handle" />;
const Out = () => <Handle type="source" position={Position.Bottom} className="wf-handle" />;

const useStatus = (id) => {
  const wf = useContext(WorkflowContext);
  return wf.mode === "test" ? wf.statuses.get(id) || { status: "pending" } : null;
};

const statusClass = (s) => (s ? `wf-${s.status}` : "");

export function TriggerNode({ data, selected }) {
  return (
    <div className={`wf-node wf-trigger ${selected ? "wf-selected" : ""}`}>
      <i className="mdi mdi-check-circle-outline wf-node-icon text-success" />
      <div className="wf-title">Trigger: {data.name}</div>
      <Out />
    </div>
  );
}

export function StepNode({ id, data, selected }) {
  const wf = useContext(WorkflowContext);
  const s = useStatus(id);
  const station = wf.stations.get(Number(data.stationId));
  return (
    <div className={`wf-node wf-step ${statusClass(s)} ${selected ? "wf-selected" : ""}`}>
      <In />
      {station?.icon && <i className={`mdi mdi-${station.icon} wf-node-icon`} />}
      {/* The station is the heading; the label given in this workflow
          goes under it (user, 2026-10-30). */}
      <div className="wf-title">
        {station ? (
          station.name
        ) : data.stationId ? (
          <span className="text-danger">Station removed</span>
        ) : (
          <span className="text-warning">No station</span>
        )}
      </div>
      {data.name && <div className="wf-label">{data.name}</div>}
      {data.note && <div className="wf-note">{data.note}</div>}
      {s && (
        <div className="wf-test nodrag" title={s.hint || undefined}>
          <span className={`wf-pill wf-pill-${s.status}`}>{STATUS_LABEL[s.status]}</span>
          <button
            type="button"
            className="btn btn-success btn-sm wf-test-btn"
            disabled={!s.canMark}
            title={s.canMark ? "Mark done" : s.hint}
            onClick={() => wf.setDone(id, true)}
          >
            Done
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm wf-test-btn"
            disabled={!s.canUnmark}
            title={s.canUnmark ? "Mark not done" : s.hint}
            onClick={() => wf.setDone(id, false)}
          >
            Undo
          </button>
        </div>
      )}
      <Out />
    </div>
  );
}

export function BranchNode({ id, data, selected }) {
  const s = useStatus(id);
  const color = BRANCH_COLORS[data.color] || BRANCH_COLORS.purple;
  return (
    <div
      className={`wf-node wf-branch ${statusClass(s)} ${selected ? "wf-selected" : ""}`}
      style={{ background: color.bg }}
    >
      <In />
      <i className={`mdi mdi-${data.icon || "source-branch"} wf-node-icon`} style={{ color: color.fg }} />
      <div className="wf-title">{data.name}</div>
      <Out />
    </div>
  );
}

export function MilestoneNode({ id, data, selected }) {
  const s = useStatus(id);
  return (
    <div className={`wf-node wf-milestone ${statusClass(s)} ${selected ? "wf-selected" : ""}`}>
      <In />
      <i className="mdi mdi-check-circle-outline wf-node-icon" />
      <div className="wf-title">{data.name}</div>
      <Out />
    </div>
  );
}

export function ReadyNode({ id, data, selected }) {
  const s = useStatus(id);
  return (
    <div className={`wf-node wf-ready ${statusClass(s)} ${selected ? "wf-selected" : ""}`}>
      <In />
      <i className="mdi mdi-check-circle-outline wf-node-icon" />
      <div className="wf-title">{data.name}</div>
      {data.note && <div className="wf-note">{data.note}</div>}
      <Out />
    </div>
  );
}

// An arrow that is easy to remove (user, 2026-10-30): a wide click area, and
// once clicked it turns blue with a red x in the middle that deletes it.
export function RemovableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  selected,
}) {
  const wf = useContext(WorkflowContext);
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const on = selected && wf.mode === "build";
  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        interactionWidth={22}
        style={on ? { ...style, stroke: "#4f46e5", strokeWidth: 2.5 } : style}
      />
      {on && (
        <EdgeLabelRenderer>
          <button
            type="button"
            className="wf-edge-remove nodrag nopan"
            title="Remove this line"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
            onClick={() => wf.removeEdge(id)}
          >
            <i className="mdi mdi-close" />
          </button>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export const edgeTypes = { removable: RemovableEdge };

export const nodeTypes = {
  trigger: TriggerNode,
  step: StepNode,
  branch: BranchNode,
  milestone: MilestoneNode,
  ready: ReadyNode,
};
